import * as React from 'react';
import { useForm, type Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CandidateSource, toHourly, type CandidateCreate, type RateUnit } from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/components/ui/toaster';
import { SkillsInput } from './SkillsInput';
import { LanguagesEditor } from './LanguagesEditor';
import { RateUnitToggle } from './RateUnitToggle';
import { DEFAULT_HOURS_PER_DAY, parseNumber, useOptions, useSettings, useSuppliers } from './api';
import type { CandidateDetail } from './types';

const formSchema = z.object({
  fullName: z.string().trim().max(200),
  label: z.string().trim().max(200),
  headline: z.string().trim().max(300),
  skills: z.array(z.string()),
  languages: z.array(z.object({ language: z.string(), level: z.string() })),
  country: z.string().trim().max(80),
  city: z.string().trim().max(120),
  email: z
    .string()
    .trim()
    .refine((v) => v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), 'Enter a valid e-mail address'),
  phone: z.string().trim().max(60),
  rateExpectation: z
    .string()
    .trim()
    .refine((v) => v === '' || (parseNumber(v) ?? -1) >= 0, 'Enter a number'),
  rateExpectationUnit: z.enum(['HOUR', 'DAY']),
  availableFrom: z.string().nullable(),
  source: z.string(),
  supplier: z.string(),
  consentDate: z.string().nullable(),
  summary: z.string(),
  role: z.string().trim().max(120),
});
type FormValues = z.infer<typeof formSchema>;

const DEFAULTS: FormValues = {
  fullName: '',
  label: '',
  headline: '',
  skills: [],
  languages: [],
  country: '',
  city: '',
  email: '',
  phone: '',
  rateExpectation: '',
  rateExpectationUnit: 'HOUR',
  availableFrom: null,
  source: '',
  supplier: '',
  consentDate: null,
  summary: '',
  role: '',
};

function toPayload(v: FormValues): CandidateCreate {
  const rate = parseNumber(v.rateExpectation);
  const text = (s: string) => (s.trim() ? s.trim() : undefined);
  return {
    fullName: text(v.fullName),
    label: text(v.label),
    headline: text(v.headline),
    skills: v.skills,
    languages: v.languages
      .map((l) => ({ language: l.language.trim(), level: l.level || 'not stated' }))
      .filter((l) => l.language.length > 0),
    country: text(v.country),
    city: text(v.city),
    email: text(v.email),
    phone: text(v.phone),
    rateExpectation: rate ?? undefined,
    rateExpectationUnit: rate !== null ? v.rateExpectationUnit : undefined,
    availableFrom: v.availableFrom ?? undefined,
    source: CandidateSource.is(v.source) ? v.source : undefined,
    supplier: v.supplier || undefined,
    consentDate: v.consentDate ?? undefined,
    summary: v.summary.trim() ? v.summary : undefined,
    role: text(v.role),
  };
}

export interface CandidateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (candidate: CandidateDetail) => void;
}

/** "New candidate" dialog → `POST /candidates` (allocates the DC-C id). */
export function CandidateFormDialog({ open, onOpenChange, onCreated }: CandidateFormDialogProps) {
  const queryClient = useQueryClient();
  const settings = useSettings();
  const options = useOptions();
  const suppliers = useSuppliers();
  const hoursPerDay = settings.data?.hours_per_day ?? DEFAULT_HOURS_PER_DAY;
  const countryListId = React.useId();

  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: DEFAULTS });

  React.useEffect(() => {
    if (open) form.reset(DEFAULTS);
  }, [open, form]);

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<CandidateDetail>('/candidates', toPayload(values)),
    onSuccess: async (candidate) => {
      toast.success(`${candidate.candidateId} created`);
      await queryClient.invalidateQueries({ queryKey: ['candidates'] });
      onOpenChange(false);
      onCreated?.(candidate);
    },
    onError: (e: Error) => {
      if (isApiError(e) && e.status === 400) {
        const errors = e.fieldErrors();
        let applied = false;
        for (const [path, message] of Object.entries(errors)) {
          const key = path.split('.')[0] as Path<FormValues>;
          if (key in DEFAULTS) {
            form.setError(key, { message });
            applied = true;
          }
        }
        if (applied) return;
      }
      toast.error(e.message);
    },
  });

  const rateValue = form.watch('rateExpectation');
  const unitValue = form.watch('rateExpectationUnit') as RateUnit;
  const rateNumber = parseNumber(rateValue);
  const hourly = rateNumber !== null && rateNumber >= 0 ? toHourly(rateNumber, unitValue, hoursPerDay) : null;

  const supplierOptions = (suppliers.data ?? []).map((p) => ({ value: p.id, label: p.name }));
  const skillSuggestions = options.data?.skill ?? [];
  const languageSuggestions = options.data?.language ?? [];
  const countrySuggestions = options.data?.country ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => !create.isPending && onOpenChange(o)}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-3xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="space-y-6" noValidate>
            <DialogHeader>
              <DialogTitle>New candidate</DialogTitle>
              <DialogDescription>
                A DC-C id is allocated on save. Only the name (as "First S.") or the id ever reaches a client.
              </DialogDescription>
            </DialogHeader>

            <Section title="Identity">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full name</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" placeholder="First Last" />
                    </FormControl>
                    <FormDescription>
                      Leave empty when the CV has no name; the profile then says "Candidate DC-C-…".
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Label</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Tech Lead AI (Amber CV)" />
                    </FormControl>
                    <FormDescription>Short internal handle shown in lists and boards.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="headline"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Headline</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Senior SAP Integration Consultant, 12 years" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Role (for the Drive folder)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Tech Lead AI" />
                    </FormControl>
                    <FormDescription>
                      Used in the folder name when there is no name yet: DC-C-…_SupplierCode_RoleShort.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>

            <Section title="Skills and languages">
              <FormField
                control={form.control}
                name="skills"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Skills</FormLabel>
                    <FormControl>
                      <SkillsInput
                        value={field.value}
                        onChange={field.onChange}
                        suggestions={skillSuggestions}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="languages"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Languages</FormLabel>
                    <FormControl>
                      <LanguagesEditor
                        value={field.value}
                        onChange={field.onChange}
                        suggestions={languageSuggestions}
                      />
                    </FormControl>
                    <FormDescription>Every language from the CV with its level.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>

            <Section title="Location and contact">
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        list={countrySuggestions.length ? countryListId : undefined}
                        placeholder="Romania"
                      />
                    </FormControl>
                    {countrySuggestions.length ? (
                      <datalist id={countryListId}>
                        {countrySuggestions.map((c) => (
                          <option key={c} value={c} />
                        ))}
                      </datalist>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Bucharest" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail (internal)</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" autoComplete="off" />
                    </FormControl>
                    <FormDescription>Internal only – never appears in a profile.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (internal)</FormLabel>
                    <FormControl>
                      <Input {...field} type="tel" autoComplete="off" />
                    </FormControl>
                    <FormDescription>Internal only – never appears in a profile.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>

            <Section title="Rate and availability">
              <FormField
                control={form.control}
                name="rateExpectation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate expectation (EUR)</FormLabel>
                    <div className="flex flex-wrap items-center gap-2">
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="any"
                          className="w-32 tabular-nums"
                        />
                      </FormControl>
                      <FormField
                        control={form.control}
                        name="rateExpectationUnit"
                        render={({ field: unitField }) => (
                          <RateUnitToggle value={unitField.value} onChange={unitField.onChange} />
                        )}
                      />
                    </div>
                    <FormDescription aria-live="polite">
                      {hourly !== null ? (
                        <span className="font-medium text-foreground">= {hourly.toFixed(2)} EUR/h</span>
                      ) : (
                        `Normalised to EUR/h (day rate ÷ ${hoursPerDay} h).`
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="availableFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Available from</FormLabel>
                    <FormControl>
                      <DateInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>

            <Section title="Origin and consent">
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        value={field.value || null}
                        onChange={(v) => field.onChange(v ?? '')}
                        options={enumOptions(CandidateSource)}
                        allowEmpty
                        emptyLabel="Not set"
                        placeholder="Not set"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supplier</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        value={field.value || null}
                        onChange={(v) => field.onChange(v ?? '')}
                        options={supplierOptions}
                        allowEmpty
                        emptyLabel="Direct / none"
                        placeholder={suppliers.isLoading ? 'Loading…' : 'Direct / none'}
                      />
                    </FormControl>
                    <FormDescription>
                      Suppliers and freelancers; intermediaries cannot supply candidates.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="consentDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Consent date</FormLabel>
                    <FormControl>
                      <DateInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>
                      GDPR consent; the expiry is computed from the retention period.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Section>

            <FormField
              control={form.control}
              name="summary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Summary (markdown)</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={5} placeholder="Strengths, gaps, notes from the first call…" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create candidate
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-4">
      <legend className="mb-3 text-sm font-semibold">{title}</legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
